import { useEffect, useState, useRef } from "react";
import {
  Building2,
  // MapPin,
  Euro,
  TrendingUp,
  // Users,
  // FileText,
  // X,
  Plus,
  Edit2,
  Trash2,
} from "lucide-react";
import { db } from "../../lib/supabase";
import { AdminActivosEditModal } from "./AdminActivosEditModal";
import { TablePagination } from "../ui/table-pagination";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
// tooltip removed to keep estado rendering consistent with AdminInversores

interface Activo {
  id: string;
  propiedad: string;
  ubicacion: string;
  tipo: "TEMPORAL" | "TRADICIONAL" | "HABITACIONES";
  inversion: number;
  valorActual: number;
  rentasAnual: number;
  hipoteca: number;
  fechaCompra: string;
  fechaPrimerAlquiler: string;
  vacancy12M: number;
  estado: "ARRAS" | "REFORMA" | "RENTING" | "CASHFLOWING";
  detalles: string;
}

// Helper to map DB row -> Activo UI model
function mapDbRowToActivo(row: any): Activo {
  const tipoMap: Record<string, Activo["tipo"]> = {
    Temporal: "TEMPORAL",
    Habitaciones: "HABITACIONES",
    "Larga Temporada": "TRADICIONAL",
  };
  const estadoMap: Record<string, Activo["estado"]> = {
    alquilado: "CASHFLOWING",
    alquilándose: "RENTING",
    "en obras": "REFORMA",
    "en venta": "ARRAS",
  };

  const vacancyStr: string = row.vacancy_12m ?? "0.0%";
  const vacancyNum =
    typeof vacancyStr === "string"
      ? parseFloat(vacancyStr.replace("%", "")) || 0
      : Number(vacancyStr) || 0;

  return {
    id: row.id ?? "",
    propiedad: row.nombre ?? "",
    ubicacion: row.ciudad ?? row.direccion ?? "",
    tipo: tipoMap[row.tipo_explotacion as string] ?? "TEMPORAL",
    inversion: Number(row.coste_total ?? 0),
    valorActual: Number(row.valor_actual ?? 0),
    rentasAnual: Number(row.rentas_anuales ?? 0),
    hipoteca: Number(row.hipoteca ?? 0),
    fechaCompra: row.fecha_compra ?? "",
    fechaPrimerAlquiler: row.fecha_primer_alquiler ?? "",
    vacancy12M: vacancyNum,
    estado: estadoMap[row.estado as string] ?? "ARRAS",
    detalles: row.notas ?? row.ver_detalle ?? "",
  };
}

export function AdminActivos() {
  const [activos, setActivos] = useState<Activo[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newActivo, setNewActivo] = useState<Partial<Activo>>({
    tipo: "TEMPORAL",
    estado: "ARRAS",
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingActivo, setEditingActivo] = useState<any | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingActivo, setDeletingActivo] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  const tableRef = useRef<HTMLTableElement | null>(null);
  const topScrollerRef = useRef<HTMLDivElement | null>(null);
  const topInnerRef = useRef<HTMLDivElement | null>(null);
  const bottomWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // Fetch activos from backend on mount
  useEffect(() => {
    const fetchActivos = async () => {
      try {
        setLoading(true);
        const res = await db.getActivosPaged(undefined, page, pageSize);
        const mapped = Array.isArray(res?.data)
          ? res.data.map(mapDbRowToActivo)
          : [];
        setActivos(mapped);
        setTotal(Number(res?.total) || 0);
      } catch (e) {
        console.error("getActivosPaged error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchActivos();
  }, [page, pageSize]);

  useEffect(() => {
    const syncWidths = () => {
      const table = tableRef.current;
      const topInner = topInnerRef.current;
      const bottom = bottomWrapperRef.current;
      if (!table || !topInner || !bottom) return;
      topInner.style.width = `${table.scrollWidth}px`;
      const isMobile = window.innerWidth < 768;
      setShowScrollHint(isMobile || bottom.scrollWidth > bottom.clientWidth);
    };

    syncWidths();
    window.addEventListener("resize", syncWidths);

    const top = topScrollerRef.current;
    const bottom = bottomWrapperRef.current;
    if (top && bottom) {
      const onTopScroll = () => { if (bottom) bottom.scrollLeft = top.scrollLeft; };
      const onBottomScroll = () => { if (top) top.scrollLeft = bottom.scrollLeft; };
      top.addEventListener("scroll", onTopScroll);
      bottom.addEventListener("scroll", onBottomScroll);
      return () => {
        top.removeEventListener("scroll", onTopScroll);
        bottom.removeEventListener("scroll", onBottomScroll);
        window.removeEventListener("resize", syncWidths);
      };
    }

    return () => window.removeEventListener("resize", syncWidths);
  }, [activos.length]);

  const handleCreateActivo = async () => {
    if (!newActivo.propiedad || !newActivo.ubicacion) {
      alert("Por favor completa los campos requeridos");
      return;
    }

    const activo: Activo = {
      id: `temp-${Date.now()}`,
      propiedad: newActivo.propiedad || "",
      ubicacion: newActivo.ubicacion || "",
      tipo: (newActivo.tipo as Activo["tipo"]) || "TEMPORAL",
      inversion: newActivo.inversion || 0,
      valorActual: newActivo.valorActual || 0,
      rentasAnual: newActivo.rentasAnual || 0,
      hipoteca: newActivo.hipoteca || 0,
      fechaCompra: newActivo.fechaCompra || "",
      fechaPrimerAlquiler: newActivo.fechaPrimerAlquiler || "",
      vacancy12M: newActivo.vacancy12M || 0,
      estado: (newActivo.estado as Activo["estado"]) || "ARRAS",
      detalles: newActivo.detalles || "",
    };

    // Optimistic UI update: add locally immediately
    setActivos([...activos, activo]);
    setIsCreateModalOpen(false);
    setNewActivo({ tipo: "TEMPORAL", estado: "ARRAS" });

    // Prepare payload for backend (exclude local numeric id)
    const tipoToDb: Record<Activo["tipo"], string> = {
      TEMPORAL: "Temporal",
      HABITACIONES: "Habitaciones",
      TRADICIONAL: "Larga Temporada",
    };
    const estadoToDb: Record<Activo["estado"], string> = {
      CASHFLOWING: "alquilado",
      RENTING: "alquilándose",
      REFORMA: "en obras",
      ARRAS: "en venta",
    };

    function toDateOnly(input: string | undefined): string | null {
      if (!input) return null;
      const d = new Date(input);
      if (isNaN(d.getTime())) return null;
      const yyyy = d.getUTCFullYear();
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const dd = String(d.getUTCDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    const payload = {
      nombre: activo.propiedad,
      ciudad: activo.ubicacion,
      direccion: activo.ubicacion,
      tipo_explotacion: tipoToDb[activo.tipo],
      coste_total: activo.inversion,
      valor_actual: activo.valorActual,
      rentas_anuales: activo.rentasAnual,
      hipoteca: activo.hipoteca,
      fecha_compra:
        toDateOnly(activo.fechaCompra) || toDateOnly(new Date().toISOString()),
      fecha_primer_alquiler: toDateOnly(activo.fechaPrimerAlquiler),
      vacancy_12m: `${(activo.vacancy12M ?? 0).toFixed(1)}%`,
      estado: estadoToDb[activo.estado],
      notas: activo.detalles || null,
    } as const;

    try {
      await db.createActivo(payload);
      // Sync with backend to replace optimistic row with the persisted record
      try {
        const data = await db.getActivos();
        const mapped = Array.isArray(data) ? data.map(mapDbRowToActivo) : [];
        setActivos(mapped);
      } catch (e) {
        console.error("post-create getActivos error:", e);
      }
    } catch (error) {
      console.error("createActivo error:", error);
      // Revert optimistic update on failure
      setActivos((prev) => prev.filter((a) => a.id !== activo.id));
      alert("No se pudo crear el activo en el servidor. Intenta de nuevo.");
    }
  };

    // Edit flow
    const openEditModal = (activo: Activo) => {
      const tipoToDb: Record<Activo["tipo"], string> = {
        TEMPORAL: "Temporal",
        HABITACIONES: "Habitaciones",
        TRADICIONAL: "Larga Temporada",
      };
      const estadoToDb: Record<Activo["estado"], string> = {
        CASHFLOWING: "alquilado",
        RENTING: "alquilándose",
        REFORMA: "en obras",
        ARRAS: "en venta",
      };

      const dbRow = {
        id: activo.id,
        nombre: activo.propiedad,
        ciudad: activo.ubicacion,
        direccion: activo.ubicacion,
        tipo_explotacion: tipoToDb[activo.tipo],
        coste_total: activo.inversion,
        valor_actual: activo.valorActual,
        rentas_anuales: activo.rentasAnual,
        hipoteca: activo.hipoteca,
        fecha_compra: activo.fechaCompra || null,
        fecha_primer_alquiler: activo.fechaPrimerAlquiler || null,
        vacancy_12m: `${(activo.vacancy12M ?? 0).toFixed(1)}%`,
        estado: estadoToDb[activo.estado],
        notas: activo.detalles || null,
      };

      setEditingActivo(dbRow);
      setIsEditModalOpen(true);
    };

    const handleSaveActivo = async (updates: Partial<any>) => {
      if (!editingActivo) return;
      setSavingEdit(true);
      try {
        await db.updateActivo(editingActivo.id, updates);
        // reload paged list
        const res = await db.getActivosPaged(undefined, page, pageSize);
        const mapped = Array.isArray(res?.data)
          ? res.data.map(mapDbRowToActivo)
          : [];
        setActivos(mapped);
        setTotal(Number(res?.total) || 0);
        setIsEditModalOpen(false);
        setEditingActivo(null);
      } catch (err) {
        console.error("updateActivo error:", err);
        alert("No se pudo actualizar el activo. Intenta de nuevo.");
      } finally {
        setSavingEdit(false);
      }
    };

  const getEstadoBadgeColor = (estado: string) => {
    // Use a consistent black pill style to match AdminInversores '% Capital Social'
    // This returns classes for a rounded pill with black background and white text.
    return "bg-black text-white rounded-full px-4 py-1 text-sm";
  };

  // getEstadoLabel removed; we keep estado display simple (same as AdminInversores)

  const openDeleteModal = (activo: Activo) => {
    setDeletingActivo(activo);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteActivo = async () => {
    if (!deletingActivo) return;
    setDeleting(true);
    try {
      await db.deleteActivo(deletingActivo.id);
      // reload paged list
      const res = await db.getActivosPaged(undefined, page, pageSize);
      const mapped = Array.isArray(res?.data)
        ? res.data.map(mapDbRowToActivo)
        : [];
      setActivos(mapped);
      setTotal(Number(res?.total) || 0);
      setIsDeleteModalOpen(false);
      setDeletingActivo(null);
    } catch (err) {
      console.error("deleteActivo error:", err);
      alert("No se pudo borrar el activo. Intenta de nuevo.");
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-3xl text-[#0F172A] mb-2"
            style={{ fontWeight: 600 }}
          >
            Activos
          </h1>
          <p className="text-gray-600">
            Gestión completa del portfolio inmobiliario
          </p>
        </div>
        <Button variant="black" onClick={() => setIsCreateModalOpen(true)}>
          <Plus size={18} className="mr-2" />
          Nuevo activo
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="flex flex-wrap gap-6">
        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm" style={{ minWidth: 'max-content', flex: '1 1 0' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  Activos gestionados
                </p>
                <p className="text-3xl" style={{ fontWeight: 600 }}>
                  {activos.length}
                </p>
              </div>
              <div className="w-14 h-14 bg-[#2B59FF]/10 rounded-full flex items-center justify-center">
                <Building2 size={20} className="text-[#2B59FF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm" style={{ minWidth: 'max-content', flex: '1 1 0' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Inversión total</p>
                <p className="text-3xl" style={{ fontWeight: 600 }}>
                  {formatCurrency(
                    activos.reduce((acc, a) => acc + a.inversion, 0)
                  )}
                </p>
              </div>
              <div className="w-14 h-14 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                <Euro size={20} className="text-[#10B981]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm" style={{ minWidth: 'max-content', flex: '1 1 0' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Rentas anuales</p>
                <p className="text-3xl" style={{ fontWeight: 600 }}>
                  {formatCurrency(
                    activos.reduce((acc, a) => acc + a.rentasAnual, 0)
                  )}
                </p>
              </div>
              <div className="w-14 h-14 bg-[#F59E0B]/10 rounded-full flex items-center justify-center">
                <TrendingUp size={20} className="text-[#F59E0B]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      {/* <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Filtros
          </Button>
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Exportar
          </Button>
        </div>
      </div> */}

      {/* Assets Table */}
      <Card className="border border-gray-200 shadow-sm w-full">
        {showScrollHint && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2 md:hidden transition-opacity duration-300 opacity-100">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500" aria-hidden="true">
              <path d="M3 12h18" />
              <path d="M7 6L3 12L7 18" />
              <path d="M17 6L21 12L17 18" />
            </svg>
            <span>Desliza horizontalmente</span>
          </div>
        )}
        <div ref={topScrollerRef} className="overflow-x-auto overflow-y-hidden" style={{ height: 16 }}>
          <div ref={topInnerRef} style={{ height: 1 }} />
        </div>
        <div ref={bottomWrapperRef} className="overflow-x-auto md:overflow-x-hidden w-full rounded-lg bg-white">
          <style>{`
            /* Activos table: wrapper-driven horizontal scroll; show all columns on mobile */
            .act-table { width: max-content; min-width: 100%; table-layout: auto; border-collapse: collapse; }
            .act-colgroup col { width: auto; }
            .act-table, .act-table th, .act-table td { box-sizing: border-box; background-clip: padding-box; }
            .act-table th, .act-table td { white-space: nowrap; padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
            /* Allow wrap on very narrow phones to avoid excessive overflow */
            @media (max-width: 480px) { .act-table th, .act-table td { white-space: normal; } }
            /* Hover should cover full row */
            .act-table tbody tr:hover td { background-color: #F9FAFB; transition: background-color 120ms ease; }
            /* Disable truncate utility inside the table so data is not clipped */
            .act-table .truncate { overflow: visible !important; text-overflow: clip !important; white-space: nowrap !important; display: inline-block !important; }
            /* Ensure actions column remains usable */
            .act-table thead th:last-child, .act-table td[data-label="Acciones"] { min-width: 120px !important; box-sizing: border-box; }
            /* Center header titles */
            .act-table thead th { text-align: center !important; }
          `}</style>

          <table ref={tableRef} className="w-full table-auto divide-y divide-gray-200 act-table">
            <colgroup className="act-colgroup">
              <col className="col-index" />
              <col className="col-name" />
              <col className="col-ubicacion" />
              <col className="col-tipo" />
              <col className="col-inversion" />
              <col className="col-valor" />
              <col className="col-rentas" />
              <col className="col-hipoteca" />
              <col className="col-fechaCompra" />
              <col className="col-fechaAlquiler" />
              <col className="col-vacancy" />
              <col className="col-estado" />
              <col className="col-actions" />
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200 bg-white">
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600, width: "3%" }}
                >
                  #
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Propiedad
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Ubicación
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Tipo
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Inversión
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Valor Actual
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Rentas Anual
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Hipoteca
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  F. Compra
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  F. Alquiler
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Vacancy
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Estado
                </th>
                <th
                  className="text-left px-6 py-4 text-xs text-gray-600 uppercase"
                  style={{ fontWeight: 600 }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading
                ? Array.from({ length: pageSize }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="animate-pulse">
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-6" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-16" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-16" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-16" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-12" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-16" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-6 bg-gray-200 rounded w-10" />
                      </td>
                    </tr>
                  ))
                : activos.map((activo, index) => (
                    <tr
                      key={activo.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4" data-label="#">
                        <span className="text-sm text-gray-600">
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-label="Propiedad">
                        <div
                          className="text-sm text-[#0F172A]"
                          style={{ fontWeight: 500 }}
                          title={activo.propiedad}
                        >
                          {activo.propiedad}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="Ubicación">
                        <div
                          className="text-sm text-gray-700"
                          title={activo.ubicacion}
                        >
                          {activo.ubicacion}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="Tipo">
                        <div
                          className="text-sm text-gray-700"
                          title={activo.tipo}
                        >
                          {activo.tipo}
                        </div>
                      </td>
                      <td className="text-sm px-6 py-4" data-label="Inversión">
                        <div
                          className="text-gray-900"
                          style={{ fontWeight: 500 }}
                          title={formatCurrency(activo.inversion)}
                        >
                          {formatCurrency(activo.inversion)}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="Valor Actual">
                        <div
                          className="text-sm text-gray-900"
                          style={{ fontWeight: 500 }}
                          title={formatCurrency(activo.valorActual)}
                        >
                          {formatCurrency(activo.valorActual)}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="Rentas Anual">
                        <div
                          className="text-sm text-gray-900"
                          style={{ fontWeight: 500 }}
                          title={formatCurrency(activo.rentasAnual)}
                        >
                          {formatCurrency(activo.rentasAnual)}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="Hipoteca">
                        <div
                          className="text-sm text-gray-900"
                          style={{ fontWeight: 500 }}
                          title={formatCurrency(activo.hipoteca)}
                        >
                          {formatCurrency(activo.hipoteca)}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="F. Compra">
                        <div className="text-sm text-gray-700">
                          {activo.fechaCompra
                            ? new Date(activo.fechaCompra).toLocaleDateString(
                                "es-ES",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                }
                              )
                            : "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="F. Alquiler">
                        <div className="text-sm text-gray-700">
                          {activo.fechaPrimerAlquiler
                            ? new Date(
                                activo.fechaPrimerAlquiler
                              ).toLocaleDateString("es-ES", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "2-digit",
                              })
                            : "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-label="Vacancy">
                        <div
                          className={`text-sm ${
                            activo.vacancy12M === 0
                              ? "text-[#10B981]"
                              : "text-gray-900"
                          }`}
                          style={{ fontWeight: 500 }}
                        >
                          {formatPercentage(activo.vacancy12M)}
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-[120px] min-w-0" data-label="Estado">
                        <div className="w-full min-w-0">
                          <div className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-black max-w-[80px] overflow-hidden min-w-0">
                            <span className="text-xs font-semibold text-white truncate block">{activo.estado}</span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center px-3 md:px-4 py-3 md:py-4" data-label="Acciones">
                        <div className="inline-flex items-center gap-2 justify-center mx-auto">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-600 hover:text-[#2B59FF] hover:bg-[#2B59FF]/10"
                            onClick={() => openEditModal(activo)}
                            aria-label={`Editar ${activo.propiedad}`}
                          >
                            <Edit2 size={18} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-600 hover:text-[#FF5757] hover:bg-[#FF5757]/10"
                            onClick={() => openDeleteModal(activo)}
                            aria-label={`Borrar ${activo.propiedad}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              {/* Totals Row */}
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4">
                  <div className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    TOTAL
                  </div>
                </td>
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4">
                  <div className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    {formatCurrency(activos.reduce((acc, a) => acc + a.inversion, 0))}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    {formatCurrency(activos.reduce((acc, a) => acc + a.valorActual, 0))}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    {formatCurrency(activos.reduce((acc, a) => acc + a.rentasAnual, 0))}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    {formatCurrency(activos.reduce((acc, a) => acc + a.hipoteca, 0))}
                  </div>
                </td>
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4">
                  {/* Estado column - keep empty for TOTAL */}
                </td>
                <td className="text-center px-3 md:px-4 py-3 md:py-4"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
          <DialogHeader className="pb-6 border-b">
            <DialogTitle
              className="text-3xl text-[#0F172A]"
              style={{ fontWeight: 600 }}
            >
              Crear nuevo activo
            </DialogTitle>
            <p className="text-gray-600 mt-2">
              Complete los datos del activo inmobiliario
            </p>
          </DialogHeader>

          <div className="space-y-8 py-8 px-2">
            {/* Row 1 - Información básica */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Información básica
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div>
                  <Label htmlFor="propiedad" className="text-sm block">
                    Propiedad *
                  </Label>
                  <Input
                    id="propiedad"
                    placeholder="Nombre de la propiedad"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.propiedad || ""}
                    onChange={(e) =>
                      setNewActivo({ ...newActivo, propiedad: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="ubicacion" className="text-sm block">
                    Ubicación *
                  </Label>
                  <Input
                    id="ubicacion"
                    placeholder="Ciudad"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.ubicacion || ""}
                    onChange={(e) =>
                      setNewActivo({ ...newActivo, ubicacion: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="tipo" className="text-sm block">
                    Tipo
                  </Label>
                  <Select
                    value={newActivo.tipo || "TEMPORAL"}
                    onValueChange={(value) =>
                      setNewActivo({
                        ...newActivo,
                        tipo: value as Activo["tipo"],
                      })
                    }
                  >
                    <SelectTrigger id="tipo" className="mt-2 h-12 w-full rounded-full bg-white border-0 px-4 shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TEMPORAL">TEMPORAL</SelectItem>
                      <SelectItem value="TRADICIONAL">TRADICIONAL</SelectItem>
                      <SelectItem value="HABITACIONES">HABITACIONES</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Row 2 - Datos financieros */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Datos financieros
              </h3>
              <div className="grid grid-cols-4 gap-8">
                <div>
                  <Label htmlFor="inversion" className="text-sm block">
                    Inversión (€)
                  </Label>
                  <Input
                    id="inversion"
                    type="number"
                    placeholder="0"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.inversion || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        inversion: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="valorActual" className="text-sm block">
                    Valor Actual (€)
                  </Label>
                  <Input
                    id="valorActual"
                    type="number"
                    placeholder="0"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.valorActual || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        valorActual: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="rentasAnual" className="text-sm block">
                    Rentas Anual (€)
                  </Label>
                  <Input
                    id="rentasAnual"
                    type="number"
                    placeholder="0"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.rentasAnual || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        rentasAnual: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="hipoteca" className="text-sm block">
                    Hipoteca (€)
                  </Label>
                  <Input
                    id="hipoteca"
                    type="number"
                    placeholder="0"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.hipoteca || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        hipoteca: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Row 3 - Fechas y métricas */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Fechas y métricas
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div>
                  <Label htmlFor="fechaCompra" className="text-sm block">
                    Fecha Compra
                  </Label>
                  <Input
                    id="fechaCompra"
                    type="date"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.fechaCompra || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        fechaCompra: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="fechaPrimerAlquiler" className="text-sm block">
                    Fecha 1º Alquiler
                  </Label>
                  <Input
                    id="fechaPrimerAlquiler"
                    type="date"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.fechaPrimerAlquiler || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        fechaPrimerAlquiler: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="vacancy12M" className="text-sm block">
                    Vacancy 12M (%)
                  </Label>
                  <Input
                    id="vacancy12M"
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    className="mt-2 h-12 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newActivo.vacancy12M || ""}
                    onChange={(e) =>
                      setNewActivo({
                        ...newActivo,
                        vacancy12M: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Row 4 - Estado y enlaces */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Estado y enlaces
              </h3>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <Label htmlFor="estado" className="text-sm block">
                    Estado
                  </Label>
                  <Select
                    value={newActivo.estado || "ARRAS"}
                    onValueChange={(value) =>
                      setNewActivo({
                        ...newActivo,
                        estado: value as Activo["estado"],
                      })
                    }
                  >
                    <SelectTrigger id="estado" className="mt-2 h-12 w-full rounded-full bg-white border-0 px-4 shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARRAS">ARRAS</SelectItem>
                      <SelectItem value="REFORMA">REFORMA</SelectItem>
                      <SelectItem value="RENTING">RENTING</SelectItem>
                      <SelectItem value="CASHFLOWING">CASHFLOWING</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="detalles" className="text-sm block">
                    Detalles (enlace Google Drive)
                  </Label>
                  <Input
                    id="detalles"
                    type="url"
                    placeholder="https://drive.google.com/..."
                    className="mt-2 h-12 rounded-full bg-[#ffffff] border-0 px-4 shadow-sm"
                    value={newActivo.detalles || ""}
                    onChange={(e) =>
                      setNewActivo({ ...newActivo, detalles: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-6 border-t gap-3">
            <Button
              variant="outline"
              className="h-12 px-8"
              onClick={() => {
                setIsCreateModalOpen(false);
                setNewActivo({ tipo: "TEMPORAL", estado: "ARRAS" });
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="black"
              className="h-12 px-8"
              onClick={handleCreateActivo}
            >
              Guardar activo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AdminActivosEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        activo={editingActivo}
        setActivo={(a) => setEditingActivo((p: any) => ({ ...(p || {}), ...(a || {}) }))}
        onSave={handleSaveActivo}
        saving={savingEdit}
      />
      {/* Delete activo modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto bg-white text-gray-900 border border-gray-200 shadow-lg">
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-2xl" style={{ fontWeight: 600 }}>
              Borrar activo
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">Confirma que deseas eliminar este activo. Esta acción no se puede deshacer.</p>
          </DialogHeader>

          <div className="py-6 px-4">
            <div className="text-sm text-gray-700">
              <div className="font-medium text-gray-900">{deletingActivo?.propiedad}</div>
              <div className="text-xs text-gray-500 mt-1">{deletingActivo?.ubicacion}</div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t gap-3">
            <Button variant="outline" className="h-12 px-6" onClick={() => setIsDeleteModalOpen(false)} disabled={deleting}>Cancelar</Button>
            <Button variant="black" className="h-12 px-6" onClick={handleDeleteActivo} disabled={deleting}>
              {deleting ? (<span className="inline-flex items-center gap-2"><Spinner size={14} /> Borrando…</span>) : ("Borrar activo")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex justify-end mt-4">
        <TablePagination
          page={page}
          totalPages={Math.max(1, Math.ceil(total / pageSize))}
          pageSize={pageSize}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => p + 1)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
